/**
 * Icon scenes for ฝ่ามือมังกรทองเก้าสวรรค์ (C0-VFX, an original art): a golden dragon in eight poses, one per move
 * (variant `v`): bursting from a palm, flying in a line, twin dragons, a head biting down (the break), rising round a
 * stamping foot (the buff), circling (the area), two palms with a dragon, a great coiled dragon (the power move).
 */
import type { Layer } from './grid';
import { burst, dot, M, palmHand, SCENES, spark } from './scenes';

const GOLD = M.gold;
const GOLD_D = M.goldDark;
const GOLD_L = '#fff0a8';
const RED = M.red;

/** A golden dragon along `pts` (24 grid, head first): a tapering body of scales, a mane, horns, an eye. */
function dragon(L: Layer, pts: ReadonlyArray<readonly [number, number]>, r0: number): void {
  const n = pts.length;
  // Body from the tail to the neck.
  for (let i = n - 1; i >= 1; i--) {
    const [x, y] = pts[i]!;
    const r = Math.max(0.8, r0 * (1 - (i / n) * 0.75));
    L.disc(x, y, r, GOLD_D);
    L.disc(x - 0.3, y - 0.3, Math.max(0.6, r - 0.7), GOLD);
    if (i % 2 === 0 && r > 1.2) L.set(x + 0.4, y - r + 0.4, GOLD_L);
    if (i % 3 === 1) L.set(x, y - r - 0.6, RED);
  }
  // The head: a bigger disc with the snout toward travel, horns back, a red eye.
  const [hx, hy] = pts[0]!;
  const [nx, ny] = pts[1] ?? [hx - 1, hy];
  const dx = hx - nx;
  const dy = hy - ny;
  const l = Math.hypot(dx, dy) || 1;
  const ux = dx / l;
  const uy = dy / l;
  L.disc(hx, hy, r0 + 0.4, GOLD_D);
  L.disc(hx - 0.2, hy - 0.2, r0 - 0.2, GOLD);
  L.disc(hx + ux * (r0 + 0.6), hy + uy * (r0 + 0.6), r0 * 0.6, GOLD);
  L.line(hx - ux * r0, hy - uy * r0 - r0 * 0.6, hx - ux * (r0 + 2.4), hy - uy * (r0 + 2.4) - r0 * 1.4, GOLD_L);
  dot(L, hx + ux * 0.6, hy + uy * 0.6 - 0.6, RED);
  // Whiskers trailing back.
  L.line(hx + ux * r0, hy + uy * r0 + 0.8, hx - ux * 1.5, hy - uy * 1.5 + 2.6, GOLD_D);
}

/** Points along a sine wave from (x0, y0) to (x1, y1), head first. */
function wave(x0: number, y0: number, x1: number, y1: number, amp: number, turns: number, n = 12): [number, number][] {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const l = Math.hypot(dx, dy) || 1;
  return Array.from({ length: n }, (_, i) => {
    const u = i / (n - 1);
    const s = Math.sin(u * Math.PI * 2 * turns) * amp * (1 - u * 0.4);
    return [x1 - dx * u - (dy / l) * s, y1 - dy * u + (dx / l) * s] as [number, number];
  });
}

/** Points round an ellipse (head first), a turn of `sweep` radians ending at angle `a1`. */
function ring(cx: number, cy: number, rx: number, ry: number, a1: number, sweep: number, n = 14): [number, number][] {
  return Array.from({ length: n }, (_, i) => {
    const a = a1 - (sweep * i) / (n - 1);
    return [cx + Math.cos(a) * rx, cy + Math.sin(a) * ry] as [number, number];
  });
}

SCENES.goldDragon = (L, x) => {
  switch (x.v) {
    case 0:
      // A dragon bursting from an open palm.
      palmHand(L, 1, 10);
      dragon(L, wave(8, 14, 21, 8, 2, 1, 11), 2.4);
      spark(L, 21, 4, 1, GOLD_L);
      break;
    case 1:
      // One dragon flying in a line to the target.
      dragon(L, wave(3, 19, 21, 5, 2.2, 1.2, 14), 2.3);
      burst(L, 21, 5, 2, 4, 6, GOLD, GOLD_D);
      break;
    case 2:
      // Twin dragons through the clouds.
      dragon(L, wave(2, 12, 21, 4, 1.6, 1, 11), 1.8);
      dragon(L, wave(3, 21, 21, 14, 1.6, 1, 11), 1.8);
      break;
    case 3:
      // The break: a dragon's head biting down through a cracked guard.
      L.arc(15, 14, 7, -1.2, 1.9, M.steelMid, 2);
      L.line(13, 8, 17, 13, M.ink);
      L.line(17, 13, 14, 19, M.ink);
      dragon(L, wave(4, 3, 14, 13, 1.4, 0.8, 10), 2.6);
      break;
    case 4:
      // The buff: a foot stamping the ground, dragons rising round it.
      L.rect(8, 18, 8, 3, M.cloth);
      L.rect(7, 21, 10, 2, M.woodDark);
      burst(L, 12, 21, 4, 7, 8, GOLD, GOLD_D, Math.PI);
      dragon(L, wave(5, 18, 4, 3, 1.4, 1.2, 10), 1.7);
      dragon(L, wave(19, 18, 20, 3, 1.4, 1.2, 10), 1.7);
      break;
    case 5:
      // The area: a dragon circling all round.
      dragon(L, ring(12, 12, 9, 6, Math.PI * 0.2, Math.PI * 1.7), 2.1);
      dot(L, 12, 12, GOLD_L);
      break;
    case 6:
      // Two palms with a dragon between them.
      palmHand(L, 0, 4);
      palmHand(L, 0, 13);
      dragon(L, wave(8, 13, 22, 11, 1.8, 1, 10), 2.2);
      break;
    default:
      // The power move: a great dragon coiled down out of the sky.
      dragon(L, [...ring(12, 9, 8, 5, -Math.PI * 0.4, Math.PI * 1.6, 12), ...wave(12, 14, 6, 22, 1.2, 0.6, 5).reverse()], 3);
      burst(L, 12, 21, 2, 5, 8, GOLD, GOLD_D);
  }
};
